// Right-hand panels + dialogs for the takeoff workspace.
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Layers, Eye, EyeOff, Plus, Trash2, Ruler, Hexagon, Hash, Box, Waves,
  ChevronDown, ChevronRight, FileOutput, Loader2, Crosshair,
  Spline, Minus, Square, Sprout, Mountain, Landmark,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MEASURE_TYPES, SCALE_PRESETS, STRUCTURE_TEMPLATES, mPerUnitForRatio, fmtQty, nzd } from "@/lib/takeoffGeo";

export const TYPE_ICONS = {
  length: Ruler, area: Hexagon, count: Hash, volume: Box, trench: Waves,
  pipe: Spline, kerb: Minus, pavement: Square, topsoil: Sprout, cutfill: Mountain, structure: Landmark,
};

// full value of a measurement incl. waste % and derived civil quantities
export const measurementValue = (m) => {
  const waste = 1 + ((m.waste_pct || 0) / 100);
  let v = (m.qty || 0) * waste * ((m.rate?.unit_rate) || 0);
  (m.derived || []).forEach((d) => { v += (d.qty || 0) * (d.rate || 0); });
  return v;
};

export const Panel = ({ title, icon: Icon, action, children, testId }) => (
  <section className="border-2 border-zinc-300 dark:border-white/15 bg-white dark:bg-[#0F141C]" data-testid={testId}>
    <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/[0.03]">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-600 dark:text-zinc-300">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#FF5F15]" />} {title}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const LAYER_COLORS = ["#f59e0b", "#38bdf8", "#a78bfa", "#34d399", "#f472b6", "#facc15", "#fb7185", "#94a3b8"];

// Civil smart-tool parameters + derived quantities editor
function MetaEditor({ m, updateMeasurement }) {
  const meta = m.meta || {};
  const setMeta = (ch) => updateMeasurement(m.id, { meta: { ...meta, ...ch } });
  const setDerivedRate = (key, rate) =>
    updateMeasurement(m.id, { derived: (m.derived || []).map((d) => (d.key === key ? { ...d, rate } : d)) });
  return (
    <div className="space-y-2">
      {m.type === "trench" && (
        <div className="grid grid-cols-2 gap-2 p-2 border border-dashed border-zinc-300 dark:border-white/15">
          <NumInput label="Bedding" value={meta.bedding_mm ?? 150} suffix="mm" onChange={(v) => setMeta({ bedding_mm: v })} />
          <NumInput label="Bulking ×" value={meta.bulking ?? 1.2} step={0.05} onChange={(v) => setMeta({ bulking: v })} />
          <NumInput label="Pipe DN" value={meta.pipe_dn_mm} suffix="mm" onChange={(v) => setMeta({ pipe_dn_mm: v })} />
          <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500 cursor-pointer self-end pb-2">
            <input type="checkbox" checked={!!meta.pipe_displacement} onChange={(e) => setMeta({ pipe_displacement: e.target.checked })} className="accent-[#FF5F15]" data-testid={`meta-displacement-${m.id}`} />
            Pipe displacement
          </label>
        </div>
      )}
      {m.type === "pipe" && (
        <div className="grid grid-cols-2 gap-2">
          <label className="block"><span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Pipe DN</span>
            <Input value={meta.dn || ""} onChange={(e) => setMeta({ dn: e.target.value })} placeholder="DN300" className="h-8 rounded-sm text-xs" /></label>
          <label className="block"><span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Material / class</span>
            <Input value={meta.material || ""} onChange={(e) => setMeta({ material: e.target.value })} placeholder="RCRRJ Class 4" className="h-8 rounded-sm text-xs" /></label>
        </div>
      )}
      {m.type === "structure" && (
        <label className="block"><span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Structure type</span>
          <select value={meta.template || ""} onChange={(e) => setMeta({ template: e.target.value })}
            className="w-full h-8 rounded-sm text-xs bg-transparent border border-zinc-300 dark:border-white/15 px-2 dark:bg-[#0F141C]" data-testid={`meta-structure-${m.id}`}>
            {STRUCTURE_TEMPLATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>
      )}
      {m.type === "pavement" && (
        <div className="p-2 border border-dashed border-zinc-300 dark:border-white/15 space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Build-up layers</div>
          {(meta.layers || []).map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_64px_64px_20px] gap-1 items-center">
              <Input value={l.name || ""} placeholder="AP40" onChange={(e) => setMeta({ layers: meta.layers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} className="h-7 rounded-sm text-[11px]" />
              <Input type="number" value={l.thickness_mm ?? ""} placeholder="mm" onChange={(e) => setMeta({ layers: meta.layers.map((x, j) => (j === i ? { ...x, thickness_mm: e.target.value === "" ? null : parseFloat(e.target.value) } : x)) })} className="h-7 rounded-sm text-[11px] font-mono" />
              <Input type="number" step="0.1" value={l.density_t_m3 ?? ""} placeholder="t/m³" onChange={(e) => setMeta({ layers: meta.layers.map((x, j) => (j === i ? { ...x, density_t_m3: e.target.value === "" ? null : parseFloat(e.target.value) } : x)) })} className="h-7 rounded-sm text-[11px] font-mono" />
              <button onClick={() => setMeta({ layers: meta.layers.filter((_, j) => j !== i) })} className="text-zinc-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          <button onClick={() => setMeta({ layers: [...(meta.layers || []), { name: "", thickness_mm: 100, density_t_m3: 2.2 }] })}
            className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-[#FF5F15]" data-testid={`meta-add-layer-${m.id}`}>+ Add layer</button>
        </div>
      )}
      {(m.derived || []).length > 0 && (
        <div className="p-2 border border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/[0.02] space-y-1" data-testid={`derived-panel-${m.id}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15]">Auto quantities</div>
          {m.derived.map((d) => (
            <div key={d.key} className="grid grid-cols-[1fr_84px_84px] gap-1 items-center text-[11px]">
              <span className="truncate">{d.name}</span>
              <span className="font-mono text-zinc-500 text-right">{fmtQty(d.qty)} {d.unit}</span>
              <div className="relative">
                <Input type="number" step="0.01" value={d.rate ?? ""} placeholder="rate"
                  onChange={(e) => setDerivedRate(d.key, e.target.value === "" ? null : parseFloat(e.target.value))}
                  className="h-6 rounded-sm text-[10px] font-mono pl-4" data-testid={`derived-rate-${m.id}-${d.key}`} />
                <span className="absolute left-1 top-1/2 -translate-y-1/2 text-[9px] text-zinc-400">$</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function LayersPanel({ layers, measurements, activeLayerId, setActiveLayerId, onChange }) {
  const counts = useMemo(() => {
    const c = {};
    (measurements || []).forEach((m) => { c[m.layer_id] = (c[m.layer_id] || 0) + 1; });
    return c;
  }, [measurements]);

  const update = (id, changes) => onChange(layers.map((l) => (l.id === id ? { ...l, ...changes } : l)));
  const remove = (l) => {
    if (counts[l.id] && !window.confirm(`"${l.name}" has ${counts[l.id]} measurement(s) — they'll be kept but unassigned. Delete layer?`)) return;
    onChange(layers.filter((x) => x.id !== l.id));
  };
  const add = () => {
    const id = crypto.randomUUID();
    onChange([...layers, { id, name: `Layer ${layers.length + 1}`, color: LAYER_COLORS[layers.length % LAYER_COLORS.length], visible: true }]);
    setActiveLayerId(id);
  };

  return (
    <Panel title="Layers" icon={Layers} testId="takeoff-layers-panel"
      action={<Button size="sm" variant="ghost" onClick={add} className="h-6 px-2 text-[10px] font-bold uppercase tracking-wider text-[#FF5F15]" data-testid="takeoff-add-layer"><Plus className="w-3 h-3 mr-1" /> Add</Button>}>
      <div className="divide-y divide-zinc-100 dark:divide-white/5">
        {layers.map((l) => (
          <div key={l.id}
            className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${l.id === activeLayerId ? "bg-[#FF5F15]/10 border-l-2 border-[#FF5F15]" : "border-l-2 border-transparent hover:bg-zinc-50 dark:hover:bg-white/[0.03]"}`}
            onClick={() => setActiveLayerId(l.id)} data-testid={`takeoff-layer-${l.id}`}>
            <label className="relative shrink-0 w-4 h-4 cursor-pointer" onClick={(e) => e.stopPropagation()}>
              <span className="absolute inset-0 rounded-sm border border-black/20" style={{ background: l.color }} />
              <input type="color" value={l.color} onChange={(e) => update(l.id, { color: e.target.value })}
                className="absolute inset-0 opacity-0 w-4 h-4 cursor-pointer" />
            </label>
            <input value={l.name} onChange={(e) => update(l.id, { name: e.target.value })} onClick={(e) => e.stopPropagation()}
              className="flex-1 min-w-0 bg-transparent text-xs font-semibold outline-none border-b border-transparent focus:border-[#FF5F15]" />
            <span className="text-[10px] text-zinc-400 font-mono">{counts[l.id] || 0}</span>
            <button onClick={(e) => { e.stopPropagation(); update(l.id, { visible: l.visible === false }); }}
              className="text-zinc-400 hover:text-zinc-200" data-testid={`takeoff-layer-vis-${l.id}`}>
              {l.visible === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
            <button onClick={(e) => { e.stopPropagation(); remove(l); }} className="text-zinc-400 hover:text-red-500">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {!layers.length && <p className="px-3 py-4 text-xs text-zinc-500">No layers — add one to organise your measurements.</p>}
      </div>
      <p className="px-3 py-2 text-[10px] text-zinc-500 border-t border-zinc-100 dark:border-white/5">New measurements go on the highlighted layer.</p>
    </Panel>
  );
}

const NumInput = ({ label, value, onChange, step = 0.1, suffix }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</span>
    <div className="relative">
      <Input type="number" step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
        className="h-8 rounded-sm text-xs font-mono pr-8" />
      {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-400">{suffix}</span>}
    </div>
  </label>
);

export function MeasurementsPanel({ layers, measurements, selectedId, setSelectedId, updateMeasurement, deleteMeasurement, drawings }) {
  const groups = useMemo(() => {
    const byLayer = {};
    (measurements || []).forEach((m) => {
      const k = layers.find((l) => l.id === m.layer_id) ? m.layer_id : "_none";
      (byLayer[k] = byLayer[k] || []).push(m);
    });
    return byLayer;
  }, [measurements, layers]);
  const drawingName = (id) => drawings.find((d) => d.id === id)?.name || "";

  const ordered = [...layers.map((l) => ({ layer: l, ms: groups[l.id] || [] })).filter((g) => g.ms.length),
    ...(groups._none?.length ? [{ layer: { id: "_none", name: "Unassigned", color: "#71717a" }, ms: groups._none }] : [])];

  return (
    <Panel title={`Measurements (${measurements.length})`} icon={Ruler} testId="takeoff-measurements-panel">
      {!measurements.length && (
        <p className="px-3 py-5 text-xs text-zinc-500">Nothing measured yet — calibrate the scale, then pick a tool on the left and click points on the drawing.</p>
      )}
      <div className="max-h-[45vh] overflow-y-auto divide-y divide-zinc-100 dark:divide-white/5">
        {ordered.map(({ layer, ms }) => (
          <div key={layer.id}>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-50 dark:bg-white/[0.03] text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              <span className="w-2 h-2 rounded-full" style={{ background: layer.color }} /> {layer.name}
            </div>
            {ms.map((m) => {
              const Icon = TYPE_ICONS[m.type] || Ruler;
              const sel = m.id === selectedId;
              const value = measurementValue(m);
              return (
                <div key={m.id} className={sel ? "bg-[#FF5F15]/5" : ""}>
                  <button onClick={() => setSelectedId(sel ? null : m.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50 dark:hover:bg-white/[0.03]"
                    data-testid={`takeoff-measurement-${m.id}`}>
                    {sel ? <ChevronDown className="w-3 h-3 text-[#FF5F15]" /> : <ChevronRight className="w-3 h-3 text-zinc-400" />}
                    <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: layer.color }} />
                    <span className="flex-1 min-w-0 truncate text-xs font-semibold">{m.name}</span>
                    {m.source === "ai" && <span className="px-1 text-[8px] font-bold uppercase text-[#FF5F15] border border-[#FF5F15]/40" title="AI assisted">✨ AI</span>}
                    <span className="font-mono text-[11px] text-zinc-500">{fmtQty(m.qty)} {m.unit}</span>
                    {value > 0 && <span className="font-mono text-[11px] font-bold text-[#FF5F15]">{nzd(value)}</span>}
                  </button>
                  {sel && (
                    <div className="px-3 pb-3 space-y-2" data-testid="takeoff-measurement-editor">
                      <div className="grid grid-cols-2 gap-2">
                        <label className="block col-span-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Name</span>
                          <Input value={m.name || ""} onChange={(e) => updateMeasurement(m.id, { name: e.target.value })}
                            className="h-8 rounded-sm text-xs" data-testid="takeoff-m-name" />
                        </label>
                        <label className="block">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Layer</span>
                          <select value={m.layer_id || ""} onChange={(e) => updateMeasurement(m.id, { layer_id: e.target.value || null })}
                            className="w-full h-8 rounded-sm text-xs bg-transparent border border-zinc-300 dark:border-white/15 px-2 dark:bg-[#0F141C]">
                            <option value="">Unassigned</option>
                            {layers.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                          </select>
                        </label>
                        <label className="block">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Drawing</span>
                          <div className="h-8 flex items-center text-[11px] text-zinc-400 truncate">{drawingName(m.drawing_id)} · p{m.page || 1}</div>
                        </label>
                        {!(m.points || []).length && (
                          <NumInput label={`Qty (${m.unit})`} value={m.qty} onChange={(v) => updateMeasurement(m.id, { qty: v })} />
                        )}
                        {["volume", "trench", "topsoil", "cutfill"].includes(m.type) && (
                          <NumInput label={m.type === "topsoil" ? "Strip depth" : m.type === "cutfill" ? "Avg depth" : "Depth"} value={m.depth} suffix="m" onChange={(v) => updateMeasurement(m.id, { depth: v })} />
                        )}
                        {m.type === "trench" && (
                          <NumInput label="Width" value={m.width} suffix="m" onChange={(v) => updateMeasurement(m.id, { width: v })} />
                        )}
                        <NumInput label="Waste %" value={m.waste_pct} suffix="%" step={1}
                          onChange={(v) => updateMeasurement(m.id, { waste_pct: v })} />
                        <NumInput label={`Rate / ${m.unit}`} value={m.rate?.unit_rate} suffix="$" step={0.01}
                          onChange={(v) => updateMeasurement(m.id, { rate: { ...(m.rate || {}), unit_rate: v } })} />
                        <NumInput label="Labour cost" value={m.rate?.labour_cost} suffix="$" step={0.01}
                          onChange={(v) => updateMeasurement(m.id, { rate: { ...(m.rate || {}), labour_cost: v } })} />
                        <NumInput label="Material cost" value={m.rate?.material_cost} suffix="$" step={0.01}
                          onChange={(v) => updateMeasurement(m.id, { rate: { ...(m.rate || {}), material_cost: v } })} />
                        <NumInput label="Plant cost" value={m.rate?.plant_cost} suffix="$" step={0.01}
                          onChange={(v) => updateMeasurement(m.id, { rate: { ...(m.rate || {}), plant_cost: v } })} />
                      </div>
                      <MetaEditor m={m} updateMeasurement={updateMeasurement} />
                      {m.ai && (
                        <div className="px-2 py-1.5 bg-zinc-100 dark:bg-white/[0.04] border border-zinc-200 dark:border-white/10 space-y-0.5" data-testid="takeoff-m-ai-evidence">
                          <p className="text-[9px] font-bold uppercase tracking-widest text-[#FF5F15]">✨ AI Assisted · {m.ai.confidence || "medium"} confidence</p>
                          {[["Drawing", m.ai.drawing_name], ["Label", m.ai.evidence?.label], ["Schedule", m.ai.evidence?.schedule],
                            ["Geometry", m.ai.evidence?.geometry], ["Rate match", m.ai.rate_match]]
                            .filter(([, v]) => v).map(([k, v]) => (
                              <p key={k} className="text-[10px] text-zinc-500"><b className="uppercase text-[9px]">{k}:</b> {v}</p>
                            ))}
                        </div>
                      )}
                      <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Notes</span>
                        <Input value={m.notes || ""} onChange={(e) => updateMeasurement(m.id, { notes: e.target.value })}
                          className="h-8 rounded-sm text-xs" placeholder="Shown as line detail on the quote" />
                      </label>
                      <div className="flex justify-between items-center pt-1">
                        <span className="text-[11px] font-mono text-zinc-500">
                          {fmtQty(m.qty)} {m.unit} × {nzd(m.rate?.unit_rate || 0)} = <b className="text-[#FF5F15]">{nzd(value)}</b>
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => deleteMeasurement(m.id)}
                          className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider text-red-500 hover:text-red-600" data-testid="takeoff-m-delete">
                          <Trash2 className="w-3 h-3 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function SummaryPanel({ layers, measurements, onExport, exportedQuote }) {
  const rows = useMemo(() => layers.map((l) => {
    const ms = measurements.filter((m) => m.layer_id === l.id);
    if (!ms.length) return null;
    const byUnit = {};
    let value = 0;
    ms.forEach((m) => {
      byUnit[m.unit] = (byUnit[m.unit] || 0) + (m.qty || 0);
      value += measurementValue(m);
    });
    return { layer: l, byUnit, value, count: ms.length };
  }).filter(Boolean), [layers, measurements]);
  const total = rows.reduce((s, r) => s + r.value, 0) +
    measurements.filter((m) => !layers.find((l) => l.id === m.layer_id))
      .reduce((s, m) => s + measurementValue(m), 0);

  return (
    <Panel title="Estimate Summary" icon={FileOutput} testId="takeoff-summary-panel">
      <div className="divide-y divide-zinc-100 dark:divide-white/5">
        {rows.map(({ layer, byUnit, value, count }) => (
          <div key={layer.id} className="px-3 py-2 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs font-semibold">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: layer.color }} /> {layer.name}
                <span className="text-[10px] text-zinc-400">({count})</span>
              </div>
              <div className="text-[10px] font-mono text-zinc-500 ml-4">
                {Object.entries(byUnit).map(([u, q]) => `${fmtQty(q)} ${u}`).join(" · ")}
              </div>
            </div>
            <span className="font-mono text-xs font-bold shrink-0">{nzd(value)}</span>
          </div>
        ))}
        {!rows.length && <p className="px-3 py-4 text-xs text-zinc-500">Quantities will roll up here per layer.</p>}
      </div>
      <div className="px-3 py-2.5 border-t-2 border-zinc-200 dark:border-white/10 flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">Est. Value (ex GST)</span>
        <span className="font-mono font-black text-lg text-[#FF5F15]" data-testid="takeoff-total-value">{nzd(total)}</span>
      </div>
      <div className="p-3 pt-0">
        <Button onClick={onExport} disabled={!measurements.length}
          className="w-full h-9 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="takeoff-export-btn">
          <FileOutput className="w-3.5 h-3.5 mr-1.5" /> Export to Quote
        </Button>
        {exportedQuote && (
          <p className="mt-1.5 text-[10px] text-zinc-500 text-center">Already exported — exporting again adds a new quote or appends lines.</p>
        )}
      </div>
    </Panel>
  );
}

export function ScaleDialog({ open, onClose, calibration, isPdf, currentScale, onApply, hasPages }) {
  const [ratio, setRatio] = useState("");
  const [known, setKnown] = useState("");
  const [allPages, setAllPages] = useState(true);
  useEffect(() => { if (open) { setRatio(""); setKnown(""); } }, [open]);

  const applyPreset = (r) => {
    onApply({ m_per_unit: mPerUnitForRatio(r), label: `1:${r}` }, allPages);
    onClose();
  };
  const applyMeasured = () => {
    const m = parseFloat(known);
    if (!m || !calibration?.dist) return toast.error("Enter the real-world length in metres");
    onApply({ m_per_unit: m / calibration.dist, label: `${m} m calibrated` }, allPages);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="takeoff-scale-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <Crosshair className="w-4 h-4 text-[#FF5F15]" /> Set Drawing Scale
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {currentScale && (
            <p className="text-xs text-zinc-500">Current scale: <b className="text-[#FF5F15]">{currentScale.label}</b>. Changing it recalculates every measurement on this page.</p>
          )}
          {isPdf && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Standard paper scale</p>
              <div className="grid grid-cols-4 gap-1.5">
                {SCALE_PRESETS.map((r) => (
                  <button key={r} onClick={() => applyPreset(r)}
                    className="h-8 border border-zinc-300 dark:border-white/15 text-xs font-mono font-bold hover:border-[#FF5F15] hover:text-[#FF5F15]"
                    data-testid={`takeoff-scale-${r}`}>1:{r}</button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input type="number" placeholder="Custom ratio e.g. 300" value={ratio} onChange={(e) => setRatio(e.target.value)}
                  className="h-8 rounded-sm text-xs" data-testid="takeoff-scale-custom" />
                <Button size="sm" variant="outline" className="h-8 rounded-sm text-[11px] font-bold uppercase"
                  onClick={() => { const r = parseFloat(ratio); if (r > 0) applyPreset(r); }}>Apply</Button>
              </div>
              <p className="text-[10px] text-zinc-500 mt-1.5">Only accurate if the PDF was exported at true paper size (e.g. A1 at 1:200). Otherwise calibrate from a known length below.</p>
            </div>
          )}
          <div className="border-t border-zinc-200 dark:border-white/10 pt-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Calibrate from a known length</p>
            {calibration?.dist ? (
              <div className="space-y-2">
                <p className="text-xs text-zinc-500">Line measured. Now enter its real length:</p>
                <div className="flex gap-2">
                  <Input type="number" step="0.01" placeholder="e.g. 25.0" value={known} onChange={(e) => setKnown(e.target.value)}
                    className="h-9 rounded-sm font-mono" autoFocus data-testid="takeoff-known-length" />
                  <span className="flex items-center text-xs text-zinc-500">metres</span>
                  <Button onClick={applyMeasured} className="h-9 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="takeoff-apply-calibration">Set Scale</Button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-zinc-500">Close this dialog, pick the <b>Calibrate</b> tool (crosshair icon) and click both ends of a dimension you know — a grid line, dimension string or scale bar — then enter its real length.</p>
            )}
          </div>
          {hasPages && (
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={allPages} onChange={(e) => setAllPages(e.target.checked)} className="accent-[#FF5F15]" />
              Apply to every page of this drawing
            </label>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ExportDialog({ open, onClose, takeoffId, measurementCount, onDone }) {
  const [mode, setMode] = useState("new"); // new | estimate | existing
  const [quotes, setQuotes] = useState(null);
  const [quoteId, setQuoteId] = useState("");
  const [groupByLayer, setGroupByLayer] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("new"); setQuoteId(""); setBusy(false);
    api.get("/quotes", { params: { status: "all" } })
      .then(({ data }) => setQuotes((data || []).filter((q) => ["draft", "sent", "viewed"].includes(q.status))))
      .catch(() => setQuotes([]));
  }, [open]);

  const run = async () => {
    if (mode === "existing" && !quoteId) return toast.error("Pick a quote to add the lines to");
    setBusy(true);
    try {
      const { data } = await api.post(`/takeoffs/${takeoffId}/export-to-quote`, {
        quote_id: mode === "existing" ? quoteId : null,
        group_by_layer: groupByLayer,
        doc_type: mode === "estimate" ? "estimate" : "quote",
      });
      onDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setBusy(false); }
  };

  const Radio = ({ value, title, desc, testId }) => (
    <button onClick={() => setMode(value)}
      className={`w-full text-left px-3 py-2.5 border-2 ${mode === value ? "border-[#FF5F15] bg-[#FF5F15]/5" : "border-zinc-200 dark:border-white/10 hover:border-zinc-400"}`}
      data-testid={testId}>
      <div className="text-xs font-bold uppercase tracking-wider">{title}</div>
      <div className="text-[11px] text-zinc-500">{desc}</div>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="takeoff-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <FileOutput className="w-4 h-4 text-[#FF5F15]" /> Export {measurementCount} Measurement{measurementCount === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Radio value="new" title="New quote" desc="Creates a draft quote pre-filled with your quantities and rates" testId="takeoff-export-new" />
          <Radio value="estimate" title="New estimate" desc="Same, but marked as an estimate rather than a formal quote" testId="takeoff-export-estimate" />
          <Radio value="existing" title="Add to existing quote" desc="Appends the lines to a draft or sent quote" testId="takeoff-export-existing" />
          {mode === "existing" && (
            <select value={quoteId} onChange={(e) => setQuoteId(e.target.value)}
              className="w-full h-9 rounded-sm text-xs bg-transparent border border-zinc-300 dark:border-white/15 px-2 dark:bg-[#0F141C]"
              data-testid="takeoff-export-quote-select">
              <option value="">{quotes === null ? "Loading quotes…" : quotes.length ? "Choose a quote…" : "No editable quotes found"}</option>
              {(quotes || []).map((q) => (
                <option key={q.id} value={q.id}>{q.quote_number} — {q.client_name || "No client"} ({q.status})</option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-2 text-xs cursor-pointer pt-1">
            <input type="checkbox" checked={groupByLayer} onChange={(e) => setGroupByLayer(e.target.checked)} className="accent-[#FF5F15]" />
            Group line items into sections by layer
          </label>
          <Button onClick={run} disabled={busy}
            className="w-full h-9 mt-1 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="takeoff-export-confirm">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Export"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
